export const createLocalEvent = () => ({
  event_id: `event_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  start_datetime: '',
  end_datetime: '',
  location_label: '',
  link: '',
  description: '',
});

export const formatEventSummary = (event) => {
  if (!event?.start_datetime) return 'Set the date and time for this event.';
  const start = new Date(event.start_datetime);
  if (Number.isNaN(start.getTime())) return 'Set the date and time for this event.';

  const dateLabel = start.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const timeLabel = start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${dateLabel} at ${timeLabel}`;
};

export const hasEventEnded = (event, now = Date.now()) => {
  if (!event?.start_datetime) return false;
  const endTime = event?.end_datetime ? new Date(event.end_datetime).getTime() : NaN;
  const startTime = new Date(event.start_datetime).getTime();
  const comparisonTime = Number.isFinite(endTime) ? endTime : startTime;
  return Number.isFinite(comparisonTime) && comparisonTime < now;
};

export const getUpcomingEventIssues = (event) => {
  const issues = [];
  const title = String(event?.title || '').trim();
  const startRaw = event?.start_datetime;
  const startTime = startRaw ? new Date(startRaw).getTime() : NaN;
  const endRaw = event?.end_datetime;
  const endTime = endRaw ? new Date(endRaw).getTime() : NaN;
  const link = String(event?.link || '').trim();

  if (!title) issues.push('Add an event title.');
  if (!startRaw) {
    issues.push('Set the start date and time.');
  } else if (Number.isNaN(startTime)) {
    issues.push('Use a valid start date and time.');
  }

  if (endRaw) {
    if (Number.isNaN(endTime)) {
      issues.push('Use a valid end date and time or leave it blank.');
    } else if (!Number.isNaN(startTime) && endTime < startTime) {
      issues.push('End time cannot be before the start time.');
    }
  }

  if (link) {
    try {
      const parsed = new URL(link);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        issues.push('Event links must start with http:// or https://.');
      }
    } catch (_) {
      issues.push('Event links must be valid URLs.');
    }
  }

  return issues;
};

export const getEventStatusMeta = (event, now = Date.now()) => {
  if (getUpcomingEventIssues(event).length > 0) {
    return {
      label: 'Needs attention',
      className: 'border-red-300 bg-red-50 text-red-700 dark:border-red-400/25 dark:bg-red-500/10 dark:text-red-300',
    };
  }

  if (hasEventEnded(event, now)) {
    return {
      label: 'Past',
      className: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-300',
    };
  }

  return {
    label: 'Upcoming',
    className: 'border-aqua/25 bg-aqua/10 text-aqua dark:border-aqua/30 dark:bg-aqua/10 dark:text-aqua',
  };
};

export const getLaunchReadinessGaps = (settings, events) => {
  const gaps = [];
  const futureEventCount = (events || []).filter(
    (event) => getUpcomingEventIssues(event).length === 0 && !hasEventEnded(event)
  ).length;

  if (!String(settings?.zoom_link || '').trim()) {
    gaps.push('Add the Zoom meeting link.');
  }
  if (!String(settings?.founding_40_start_date || '').trim()) {
    gaps.push('Set the Founding 40 start date so launch timing is visible.');
  }
  if (!String(settings?.founding_40_time || '').trim()) {
    gaps.push('Set the Founding 40 time so public launch details are complete.');
  }
  if (futureEventCount === 0) {
    gaps.push('Add at least one future event so public upcoming-event surfaces have live launch details.');
  }

  return gaps;
};

export const sortUpcomingEvents = (events) => [...events].sort((a, b) => {
  const aPast = hasEventEnded(a);
  const bPast = hasEventEnded(b);
  if (aPast !== bPast) return aPast ? 1 : -1;

  const aTime = a?.start_datetime ? new Date(a.start_datetime).getTime() : Number.POSITIVE_INFINITY;
  const bTime = b?.start_datetime ? new Date(b.start_datetime).getTime() : Number.POSITIVE_INFINITY;

  if (aPast && bPast) {
    if (aTime !== bTime) return bTime - aTime;
  } else if (aTime !== bTime) {
    return aTime - bTime;
  }

  return String(a?.title || '').localeCompare(String(b?.title || ''));
});

const FOUNDING_40_TITLE_PATTERN = /founding\s*40/i;
const FOUNDING_40_REUNION_PATTERN = /reunion|feedback|give input/i;

const parseTimeLabel = (value) => {
  const match = String(value || '').trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\b/i);
  if (!match) return null;

  const [, rawHours, rawMinutes = '00', meridiem] = match;
  let hours = Number(rawHours);
  const minutes = Number(rawMinutes);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes < 0 || minutes > 59) {
    return null;
  }

  if (meridiem.toUpperCase() === 'PM' && hours < 12) hours += 12;
  if (meridiem.toUpperCase() === 'AM' && hours === 12) hours = 0;
  if (hours < 0 || hours > 23) return null;

  return { hours, minutes };
};

const buildIsoFromDateAndClockLabel = (dateValue, clockLabel) => {
  if (!dateValue) return '';
  const parsedTime = parseTimeLabel(clockLabel);
  if (!parsedTime) return '';

  const pad = (part) => String(part).padStart(2, '0');
  const next = new Date(`${dateValue}T${pad(parsedTime.hours)}:${pad(parsedTime.minutes)}`);
  return Number.isNaN(next.getTime()) ? '' : next.toISOString();
};

const isFounding40Event = (event) => FOUNDING_40_TITLE_PATTERN.test(String(event?.title || ''));
const isFounding40ReunionEvent = (event) => FOUNDING_40_REUNION_PATTERN.test(String(event?.title || ''));

const eventsDiffer = (left, right) => JSON.stringify(left) !== JSON.stringify(right);

export const syncFounding40Events = ({
  events,
  founding40SeriesDates,
  founding40Time,
}) => {
  const baseEvents = Array.isArray(events) ? events : [];
  const nextEvents = [...baseEvents];
  const nextDates = Array.isArray(founding40SeriesDates)
    ? founding40SeriesDates.map((item) => String(item || '').trim()).filter(Boolean)
    : [];

  if (nextEvents.length === 0 || nextDates.length === 0) {
    return baseEvents;
  }

  const futureFounding40Events = sortUpcomingEvents(
    nextEvents.filter((event) => isFounding40Event(event) && !hasEventEnded(event))
  );

  if (futureFounding40Events.length === 0) {
    return baseEvents;
  }

  const updatesById = new Map();

  futureFounding40Events.forEach((event, index) => {
    const nextDate = nextDates[index];
    if (!nextDate || !event?.event_id) return;

    const currentStart = event?.start_datetime ? new Date(event.start_datetime) : null;
    const currentStartLabel =
      currentStart && !Number.isNaN(currentStart.getTime())
        ? currentStart.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        : '';
    const nextTimeLabel = isFounding40ReunionEvent(event)
      ? currentStartLabel || founding40Time
      : founding40Time;
    const nextStart = buildIsoFromDateAndClockLabel(nextDate, nextTimeLabel);
    if (!nextStart) return;

    const currentStartTime = event?.start_datetime ? new Date(event.start_datetime).getTime() : NaN;
    const currentEnd = event?.end_datetime ? new Date(event.end_datetime).getTime() : NaN;
    const durationMs =
      Number.isFinite(currentStartTime) && Number.isFinite(currentEnd) && currentEnd >= currentStartTime
        ? currentEnd - currentStartTime
        : null;
    const nextStartTime = new Date(nextStart).getTime();
    const nextEnd =
      durationMs !== null && Number.isFinite(nextStartTime)
        ? new Date(nextStartTime + durationMs).toISOString()
        : event?.end_datetime || '';

    updatesById.set(event.event_id, {
      ...event,
      start_datetime: nextStart,
      end_datetime: nextEnd,
    });
  });

  if (updatesById.size === 0) {
    return baseEvents;
  }

  const syncedEvents = sortUpcomingEvents(
    nextEvents.map((event) => updatesById.get(event.event_id) || event)
  );

  return eventsDiffer(syncedEvents, baseEvents) ? syncedEvents : baseEvents;
};

const splitDateTimeParts = (value) => {
  if (!value) {
    return { date: '', time: '' };
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: '', time: '' };
  }

  const pad = (part) => String(part).padStart(2, '0');
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const toIsoFromDateAndTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return '';
  const next = new Date(`${dateValue}T${timeValue}`);
  return Number.isNaN(next.getTime()) ? '' : next.toISOString();
};

export const buildEventDraft = (event = createLocalEvent()) => {
  const start = splitDateTimeParts(event.start_datetime);
  const end = splitDateTimeParts(event.end_datetime);

  return {
    event_id: event.event_id || createLocalEvent().event_id,
    title: event.title || '',
    date: start.date,
    start_time: start.time,
    end_time: end.time,
    location_label: event.location_label || '',
    link: event.link || '',
    description: event.description || '',
    ...(event.theme ? { theme: event.theme } : {}),
  };
};

export const draftToEvent = (draft) => ({
  event_id: draft.event_id || createLocalEvent().event_id,
  title: draft.title || '',
  start_datetime: toIsoFromDateAndTime(draft.date, draft.start_time),
  end_datetime: draft.end_time ? toIsoFromDateAndTime(draft.date, draft.end_time) : '',
  location_label: draft.location_label || '',
  link: draft.link || '',
  description: draft.description || '',
  ...(draft.theme ? { theme: draft.theme } : {}),
});
